/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-undef */
import axios from 'axios';
import React from 'react';
import ReactDOM from 'react-dom';
import cookies from 'react-cookies';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { apiJdigUrl, domainCookieUrl } from '@/enum/config';
import { message } from 'antd';
import { Decrypt, Encrypt } from './AES';
import { formatLanguage } from '@/util/index';
import {
  clearHomepageInitHandledForCurrentLogin,
  clearHomepageInitPending,
  clearHomepageStatusFailedInCurrentLogin,
} from '@/util/homepageInitState';
import { clearHomePageConfigCache } from '@/util/homepageConfig';
import { clearCartNumCache, clearPersonInfoCache } from '@/api/newApi/carts';
import { clearAuthorityCache, clearCategoryTreeCache } from '@/api/newApi/header';
import { clearAddressCache } from '@/api/newApi/personalCenter/address';
import { clearUserConfigCache } from '@/api/newApi/user';
import { resetSessionBootstrap } from '@/util/sessionBootstrap';
import { appHistory } from '@/util/appHistory';
import { preloadForbidden403Image } from '@/util/forbidden403Config';

const validLanguage = formatLanguage();

// 403 跳转去重：无权限页常并发多个接口、可能同时返回 403，
// 用时间窗去重，保证一次「无权限突发」只做一次 SPA 跳转，避免重复导航与 loading 叠加
let lastForbiddenRedirectAt = 0;
const publicPaths = ['/passport/login', '/customerregistration', '/register'];
const UNAUTHORIZED_REDIRECT_DEDUPE_WINDOW = 3000;
let unauthorizedRedirecting = false;
let lastUnauthorizedRedirectAt = 0;

let requestCount = 0; //累计loading次数
function showLoading() {
  if (requestCount == 0) {
    const dom = document.createElement('div');
    const dom1 = document.createElement('div');
    dom.setAttribute('id', 'loading');
    dom1.setAttribute('class', 'loadwrapper');
    document.body.appendChild(dom);
    dom.appendChild(dom1);
    ReactDOM.render(
      React.createElement(Spin, {
        size: 'large',
        indicator: React.createElement(LoadingOutlined, {
          style: {
            color: 'var(--ui-primary-color, #E1251B)',
            fontSize: 24,
          },
        }) as any,
      }),
      dom1,
    );
  }
  requestCount++;
}
function hideLoading() {
  requestCount = Math.max(0, requestCount - 1);
  if (requestCount == 0) {
    const doms = document.getElementById('loading');
    if (doms?.parentNode) {
      doms.parentNode.removeChild(doms);
    }
  }
}
axios.defaults.timeout = 100000;
axios.defaults.withCredentials = true;

const saveCookie = (key: any, val: any) => {
  cookies.save(key, val, {
    domain: domainCookieUrl,
    path: '/',
    // sameSite:'none'
  });
};

// 检测当前链接是否包含https或者http，如果有就不做单独处理了
const checkUrl = (url: any) => {
  return /^(http|https):/.test(url);
};

const isPublicPath = (pathname: string): boolean =>
  publicPaths.some(path => pathname === path || pathname.startsWith(`${path}/`));

const isLoadingEnabled = (headers: unknown): boolean => {
  if (!headers || typeof headers !== 'object') {
    return true;
  }

  return (headers as { isLoading?: unknown }).isLoading !== false;
};

const clearLoginStateCaches = () => {
  clearHomepageInitHandledForCurrentLogin();
  clearHomepageInitPending();
  clearHomepageStatusFailedInCurrentLogin();
  clearHomePageConfigCache();
  clearPersonInfoCache();
  clearCartNumCache();
  clearAddressCache();
  clearUserConfigCache();
  clearAuthorityCache();
  clearCategoryTreeCache();
  resetSessionBootstrap();
};

const redirectToLoginOnce = () => {
  const now = Date.now();
  if (unauthorizedRedirecting || now - lastUnauthorizedRedirectAt <= UNAUTHORIZED_REDIRECT_DEDUPE_WINDOW) {
    return;
  }

  // 并发接口同时 401 时只清一次登录态缓存并触发一次跳转，避免接口风暴继续放大。
  unauthorizedRedirecting = true;
  lastUnauthorizedRedirectAt = now;
  clearLoginStateCaches();
  window._openPage('/passport/login', '_self');
  window.setTimeout(() => {
    unauthorizedRedirecting = false;
  }, UNAUTHORIZED_REDIRECT_DEDUPE_WINDOW);
};
/**
 * http request 拦截器
 */
axios.interceptors.request.use(
  config => {
    const hostname = window.location.hostname;
    if (unauthorizedRedirecting && !isPublicPath(window.location.pathname)) {
      return Promise.reject(new Error('UNAUTHORIZED_REDIRECTING'));
    }

    if (isLoadingEnabled(config.headers)) {
      console.log('showLoading');
      showLoading();
    }
    saveCookie('domainUrl', hostname);
    // config.data = JSON.stringify(config.data);
    config.headers = {
      ...config.headers,
      // 'Content-Type': 'text/plain',
      'X-Requested-With': 'XMLHttpRequest',
      'X-jd-ts': new Date().getTime(),
      'JDI-Language': validLanguage,
    };

    if (!checkUrl(config.url)) {
      config.url = 'https://' + apiJdigUrl + config?.url;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

/**
 * http response 拦截器
 */
axios.interceptors.response.use(
  response => {
    if (isLoadingEnabled(response.config.headers)) {
      hideLoading();
    }
    return response;
  },
  error => {
    if (error?.config && isLoadingEnabled(error.config.headers)) {
      hideLoading();
    }
    const { pathname } = window.location;

    if (
      error.response &&
      error.response.status == 401 &&
      !isPublicPath(pathname)
    ) {
      redirectToLoginOnce();
    }

    // 登录/注册等公共页的 403 属业务失败（如账号无权限、风控拦截），应由页面自身提示，
    // 不能全局劫持跳 /403，否则与登录态校验相互弹跳形成死循环
    if (
      error.response &&
      error.response.status == 403 &&
      pathname !== '/403' &&
      !isPublicPath(pathname)
    ) {
      const now = Date.now();
      // SPA 内跳转替代 window.open('_self')：不整页重载、不二次 bootstrapI18n，403 页几乎瞬时呈现
      // 1s 时间窗去重，避免并发 403 重复 replace（appHistory 与 router 复用同一实例）
      if (appHistory.location.pathname !== '/403' && now - lastForbiddenRedirectAt > 1000) {
        lastForbiddenRedirectAt = now;
        preloadForbidden403Image();
        appHistory.replace('/403');
      }
    }
    // console.log("请求出错：", error);
    return Promise.reject(error);
  },
);

/**
 * 封装get方法
 * @param url  请求url
 * @param params  请求参数
 * @returns {Promise}
 */
export function get(url: string, params = {}, isLoading?: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        params: params,
        withCredentials: true,
        headers: {
          isLoading: isLoading,
          // encrypt: 0,
        },
      })
      .then(response => {
        resolve(response ? response.data : '');
      })
      .catch(error => {
        reject(error);
      });
  });
}

/**
 * 封装post请求
 * @param url
 * @param data
 * @returns {Promise}
 */

export function post(url: string, data: any, isLoading?: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    for (const key in data) {
      formData.append(key, data[key]);
    }
    axios
      .post(url, formData, {
        headers: {
          isLoading: isLoading,
          // encrypt: 0,
        },
      })
      .then(
        response => {
          //关闭进度条
          resolve(response.data);
        },
        err => {
          reject(err);
        },
      );
  });
}

/**
 * 封装post请求
 * @param url
 * @param data
 * @returns {Promise}
 */

export function jsonPost(url: string, data: any, isLoading?: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    axios
      .post(url, data, {
        headers: {
          'Content-Type': 'application/json',
          isLoading: isLoading,
          // encrypt: 0,
        },
      })
      .then(
        response => {
          //关闭进度条
          resolve(response?.data);
        },
        err => {
          reject(err);
        },
      );
  });
}
/**
 * 封装jsondownload请求
 * @param url
 * @param data
 * @returns {Promise}
 */

export function jsonDownload(url: string, data: any, isLoading?: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    axios
      .post(url, data, {
        headers: {
          'Content-Type': 'application/json',
          isLoading: isLoading,
        },
        responseType: 'blob',
      })
      .then(
        response => {
          let filename = response.headers['content-disposition'].split('filename=')[1] || '';
          filename = decodeURI(filename);
          //关闭进度条
          resolve({ blob: new Blob([response.data]), filename: filename });
        },
        err => {
          reject(err);
        },
      );
  });
}
/**
 * 封装download请求
 * @param url
 * @param data
 * @returns {Promise}
 */

export function download(url: string, data: any, isLoading?: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    for (const key in data) {
      formData.append(key, data[key]);
    }
    axios
      .post(url, formData, {
        headers: {
          isLoading: isLoading,
        },
        responseType: 'blob',
      })
      .then(
        response => {
          let filename = response.headers['content-disposition']
            ? response.headers['content-disposition'].split('filename=')[1] || ''
            : '';
          filename = decodeURI(filename);
          //关闭进度条
          resolve({ blob: new Blob([response.data]), filename: filename });
        },
        err => {
          reject(err);
        },
      );
  });
}

//统一接口处理，返回数据
export default function http(fecth: any, url: any, param: {} | undefined, isLoading?: boolean, ase?: any) {
  const ASE = ase;
  return new Promise((resolve, reject) => {
    switch (fecth) {
      case 'get':
        // console.log('param', url ,param)
        if (ASE && param && Object.keys(param || {}).length !== 0) {
          let query = '';
          const p = param as any;
          const keys = Object.keys(p);
          keys.forEach((item: any, index: any) => {
            query = query + item + '=' + p[item] + (index == keys.length - 1 ? '' : '&');
          });
          param = { param: encodeURIComponent(Encrypt(query)) };
        }
        get(url, param, isLoading)
          .then(function (response) {
            if (response.success || response?.message == 'success') {
              const newRes = response?.value
                ? { ...response, value: ASE && response.value ? JSON.parse(Decrypt(response.value)) : response.value }
                : response;
              // console.error(url, newRes)
              resolve(newRes);
              return;
            }
            resolve(response);
          })
          .catch(function (error) {
            console.log('get request GET failed.', error);
            reject(error);
          });
        break;
      case 'post':
        // console.log('param',url , param)
        if (ASE && param && Object.keys(param)?.length !== 0) {
          param = { param: Encrypt(param) };
        }
        post(url, param, isLoading)
          .then(function (response) {
            if (response.success || response?.message == 'success') {
              const newRes = {
                ...response,
                value: ASE && response.value ? JSON.parse(Decrypt(response.value)) : response.value,
              };
              // console.error(url, newRes)
              resolve(newRes);
              return;
            }

            resolve(response);
          })
          .catch(function (error) {
            console.log('get request POST failed.', error);
            reject(error);
          });
        break;
      case 'jsonpost':
        // console.log('param', url ,param)
        if (ASE && param && Object.keys(param)?.length !== 0) {
          param = { param: Encrypt(param) };
        }
        jsonPost(url, param, isLoading)
          .then(function (response) {
            if (response.success || response?.message == 'success') {
              const newRes = {
                ...response,
                value: ASE && response.value ? JSON.parse(Decrypt(response.value)) : response.value,
              };
              // console.log(url, newRes)
              resolve(newRes);
              return;
            }
            resolve(response);
          })
          .catch(function (error) {
            // console.error('get request POST failed.', error);
            reject(error);
          });
        break;
      case 'download':
        download(url, param, isLoading)
          .then(function (response) {
            resolve(response);
          })
          .catch(function (error) {
            console.log('get request POST failed.', error);
            reject(error);
          });
        break;
      case 'jsonDownload':
        jsonDownload(url, param, isLoading)
          .then(function (response) {
            resolve(response);
          })
          .catch(function (error) {
            console.log('get request POST failed.', error);
            reject(error);
          });
        break;
      default:
        break;
    }
  });
}

export function messageHttp(method: string, url: string, params: object, isLoading?: boolean) {
  return new Promise((resolve, reject) => {
    http(method, url, params, isLoading).then(
      (res: any) => {
        if (res.success || res?.message == 'success') {
          resolve(res);
        } else {
          message.error(res.msg || res.message);
          resolve(res);
        }
      },
      error => {
        console.log('网络异常~', error);
        message.error('网络异常~');
        reject({ msg: '网络异常~' });
      },
    );
  });
}
